import { Loadable } from '@/components/loaders';
import { DeleteCampaignModal } from '@/features/campaigns/components/CampaignDetails/CampaignSettings/parts';
import RouteConstants from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useWorkspaceAdmin } from '@/features/workspaces/hooks/useWorkspace';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { useModalContext } from '@/hooks/useModalContext';
import { Button, EditIcon, IconButton, TrashIcon } from '@/lib';
import { useGetCampaignV2Query } from '@/services/endpoints/campaigns';
import { CampaignV2Response } from '@/services/endpoints/campaigns/types';
import { format } from 'date-fns';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IoArrowBack } from 'react-icons/io5';
import { useHistory, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { mapCampaignStatus, STATUS_LABELS, STATUS_STYLES } from '../../../Whatsapp/Campaigns/campaignStatus';
import { CampaignsCreditReservationPanel } from '@/features/campaigns/v2/credits';

const MINIMUM_LOADING_DURATION = 400;
const EMPTY_VALUE = '—';

const formatNumber = (value?: number) => (value === undefined || value === null ? EMPTY_VALUE : new Intl.NumberFormat('en-US').format(value));

const formatDateTime = (iso?: string) => {
  if (!iso) return EMPTY_VALUE;
  try {
    return format(new Date(iso), 'yyyy-MM-dd HH:mm');
  } catch {
    return EMPTY_VALUE;
  }
};

const computePercent = (numerator?: number, denominator?: number) => {
  if (!denominator || numerator === undefined || numerator === null) return undefined;
  return Math.round((numerator / denominator) * 100);
};

type StatTileProps = {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  emphasis?: 'default' | 'positive' | 'warning' | 'danger';
};

const EMPHASIS_STYLES: Record<NonNullable<StatTileProps['emphasis']>, string> = {
  default: 'text-slate-900',
  positive: 'text-emerald-700',
  warning: 'text-amber-700',
  danger: 'text-red-700',
};

const StatTile: FC<StatTileProps> = ({ label, value, caption, emphasis = 'default' }) => (
  <div className='flex flex-col gap-1 rounded-2xl border border-secondary-border bg-white-100 p-4'>
    <p className='text-xs font-semibold uppercase tracking-[0.12em] text-secondary-500'>{label}</p>
    <p className={twMerge('text-2xl font-semibold', EMPHASIS_STYLES[emphasis])}>{value}</p>
    {caption ? <p className='text-xs text-secondary-500'>{caption}</p> : null}
  </div>
);

type FieldRowProps = {
  label: string;
  children: ReactNode;
};

const FieldRow: FC<FieldRowProps> = ({ label, children }) => (
  <div className='flex flex-col gap-1'>
    <p className='text-xs font-semibold uppercase tracking-[0.12em] text-secondary-500'>{label}</p>
    <div className='text-sm text-slate-900'>{children}</div>
  </div>
);

type DetailsHeaderProps = {
  campaign: CampaignV2Response;
};

const DetailsHeader: FC<DetailsHeaderProps> = ({ campaign }) => {
  const { t } = useTranslation('campaign');
  const history = useHistory();
  const { openModal } = useModalContext();
  const isWorkspaceAdmin = useWorkspaceAdmin();
  const status = mapCampaignStatus(campaign.status);

  const handleEdit = () => {
    history.push(RouteConstants.editCampaignSms.replace(':campaignId', campaign.id));
  };

  const handleDelete = () => {
    openModal({
      renderContent: DeleteCampaignModal,
      renderContentProps: {
        campaign,
        redirectAfterDelete: RouteConstants.smsCampaigns,
      },
      dismissable: true,
      fullWidth: false,
      fullHeight: false,
    });
  };

  return (
    <header className='flex flex-col gap-5 rounded-3xl border border-secondary-border bg-white-100 p-6 shadow-sm md:p-8'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div className='flex min-w-0 flex-col gap-3'>
          <div className='flex flex-wrap items-center gap-3'>
            <h1 className='truncate text-3xl font-semibold tracking-tight text-slate-900'>{campaign.name}</h1>
            <span
              className={twMerge(
                'inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]',
                STATUS_STYLES[status]
              )}
            >
              {STATUS_LABELS[status]}
            </span>
            <span className='inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-800'>
              {campaign.providerType}
            </span>
          </div>
          <p className='max-w-3xl text-sm text-secondary-600'>
            {campaign.description ? campaign.description : t('smsCampaignDetails.noDescription')}
          </p>
        </div>

        <div className='flex shrink-0 items-center gap-2'>
          <IconButton
            type='button'
            intent='secondary'
            text={t('smsCampaignDetails.editCampaign')}
            leadingIcon={<EditIcon className='size-4' />}
            onClick={handleEdit}
            aria-label={t('smsCampaignDetails.editCampaign')}
          />
          {isWorkspaceAdmin ? (
            <IconButton
              type='button'
              intent='link-grey'
              className='text-red-600 hover:text-red-800'
              text={t('smsCampaignDetails.deleteCampaign')}
              leadingIcon={<TrashIcon className='size-4' />}
              onClick={handleDelete}
              aria-label={t('smsCampaignDetails.deleteCampaign')}
            />
          ) : null}
        </div>
      </div>
    </header>
  );
};

type PerformanceGridProps = {
  campaign: CampaignV2Response;
};

const PerformanceGrid: FC<PerformanceGridProps> = ({ campaign }) => {
  const { t } = useTranslation('campaign');
  const stats = campaign.stats;
  const sent = stats?.totalSent;
  const delivered = stats?.totalDelivered;
  const read = stats?.totalRead;
  const deliveredPercent = computePercent(delivered, sent);
  const readPercent = computePercent(read, delivered);

  const buildCaption = (percent?: number) => (percent === undefined ? undefined : `${percent}%`);

  return (
    <section className='flex flex-col gap-4 rounded-3xl border border-secondary-border bg-surface p-6 shadow-sm md:p-8'>
      <div className='flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between'>
        <div>
          <h2 className='text-lg font-semibold text-slate-900'>{t('smsCampaignDetails.stats.title')}</h2>
          {stats?.lastUpdatedAt ? (
            <p className='text-xs text-secondary-500'>
              {t('smsCampaignDetails.stats.lastUpdatedAt', { date: formatDateTime(stats.lastUpdatedAt) })}
            </p>
          ) : null}
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4'>
        <StatTile label={t('smsCampaignDetails.stats.totalSent')} value={formatNumber(sent)} />
        <StatTile
          label={t('smsCampaignDetails.stats.totalDelivered')}
          value={formatNumber(delivered)}
          caption={buildCaption(deliveredPercent)}
          emphasis='positive'
        />
        <StatTile
          label={t('smsCampaignDetails.stats.totalRead')}
          value={formatNumber(read)}
          caption={buildCaption(readPercent)}
          emphasis='positive'
        />
        <StatTile label={t('smsCampaignDetails.stats.totalClicked')} value={formatNumber(stats?.totalClicked)} emphasis='positive' />
        <StatTile label={t('smsCampaignDetails.stats.totalFailed')} value={formatNumber(stats?.totalFailed)} emphasis='danger' />
        <StatTile label={t('smsCampaignDetails.stats.totalBounced')} value={formatNumber(stats?.totalBounced)} emphasis='warning' />
        <StatTile
          label={t('smsCampaignDetails.stats.totalUnsubscribed')}
          value={formatNumber(stats?.totalUnsubscribed)}
          emphasis='warning'
        />
      </div>
    </section>
  );
};

type DetailsCardProps = {
  campaign: CampaignV2Response;
};

const DetailsCard: FC<DetailsCardProps> = ({ campaign }) => {
  const { t } = useTranslation('campaign');
  const isScheduled = Boolean(campaign.scheduledStart);

  return (
    <section className='rounded-3xl border border-secondary-border bg-surface p-6 shadow-sm md:p-8'>
      <div className='grid gap-6 sm:grid-cols-2'>
        <FieldRow label={t('smsCampaignDetails.campaignId')}>
          <span className='break-all font-mono text-xs text-slate-900'>{campaign.id}</span>
        </FieldRow>
        <FieldRow label={t('smsCampaignDetails.audience')}>
          <span className='break-all font-mono text-xs text-slate-900'>{campaign.audienceId ?? EMPTY_VALUE}</span>
        </FieldRow>
        <FieldRow label={t('smsCampaignDetails.provider')}>
          <span className='inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-800'>
            {campaign.providerType}
          </span>
        </FieldRow>
        <FieldRow label={t('smsCampaignDetails.schedule')}>
          {isScheduled ? (
            <div className='flex flex-col gap-1'>
              <span>
                <span className='font-semibold'>{t('scheduleStart')}: </span>
                {formatDateTime(campaign.scheduledStart)}
              </span>
              <span>
                <span className='font-semibold'>{t('scheduleEnd')}: </span>
                {formatDateTime(campaign.scheduledEnd)}
              </span>
            </div>
          ) : (
            <span className='inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700'>
              {t('smsCampaignDetails.sendNowSchedule')}
            </span>
          )}
        </FieldRow>
        <FieldRow label={t('smsCampaignDetails.connectorInstance')}>
          <span className='break-all font-mono text-xs text-slate-900'>{campaign.connectorInstanceId ?? EMPTY_VALUE}</span>
        </FieldRow>
        <FieldRow label={t('smsCampaignDetails.flowInstance')}>
          <span className='break-all font-mono text-xs text-slate-900'>{campaign.connectorFlowInstanceId ?? EMPTY_VALUE}</span>
        </FieldRow>
        <FieldRow label={t('smsCampaignDetails.createdOn')}>{formatDateTime(campaign.createdOn)}</FieldRow>
        <FieldRow label={t('smsCampaignDetails.updatedOn')}>{formatDateTime(campaign.updatedOn)}</FieldRow>
      </div>
    </section>
  );
};

export const SmsCampaignDetails: FC = () => {
  const { t } = useTranslation('campaign');
  const history = useHistory();
  const workspace = useWorkspace();
  const { campaignId } = useParams<{ campaignId: string }>();

  const {
    data: campaign,
    isLoading,
    isFetching,
    isError,
    isUninitialized,
  } = useGetCampaignV2Query({ workspaceId: workspace.id, campaignId }, { skip: !workspace.id || !campaignId });

  const isLoadingDelayed = useDelayedBoolean(isLoading || isFetching, MINIMUM_LOADING_DURATION);
  const shouldShowLoading = isLoading || isLoadingDelayed || isUninitialized;

  const handleBack = () => history.push(RouteConstants.smsCampaigns);

  const showNotFound = !shouldShowLoading && (isError || !campaign);

  return (
    <div className='flex flex-col gap-5'>
      <button
        type='button'
        onClick={handleBack}
        className='inline-flex w-fit items-center gap-2 text-sm font-semibold text-secondary-700 hover:text-slate-900'
      >
        <IoArrowBack className='size-4' />
        {t('smsCampaignDetails.backToList')}
      </button>

      <Loadable isLoading={shouldShowLoading}>
        {showNotFound ? (
          <section className='flex flex-col items-start gap-4 rounded-3xl border border-red-200 bg-red-50 p-8 shadow-sm'>
            <h2 className='text-lg font-semibold text-red-800'>{t('smsCampaignDetails.notFound')}</h2>
            <Button intent='secondary' text={t('smsCampaignDetails.backToList')} onClick={handleBack} />
          </section>
        ) : campaign ? (
          <>
            <DetailsHeader campaign={campaign} />
            <CampaignsCreditReservationPanel campaignId={campaign.id} />
            <PerformanceGrid campaign={campaign} />
            <DetailsCard campaign={campaign} />
          </>
        ) : null}
      </Loadable>
    </div>
  );
};
